<template>
  <div class="panel panel-default">
    <div class="panel-heading">
      <h5 class="pull-left">
        {{ title }}
      </h5>

      <button v-if="showCompareButton" type="button" class="btn btn-xs btn-black pull-right"
              @click="$emit('toggle-comparison')" style="margin-top: 7px">
        <i class="fa fa-search"></i> {{ display ? 'Disable' : 'Enable' }}
      </button>

      <div class="clearfix"></div>
    </div>

    <div v-if="display" class="panel-body">
      <form @submit.prevent="$emit('submit')">
        <div class="row">
          <div class="col-xs-4">
            <DateFormGroup
                label="Sold From"
                :value="filters.sold_from"
                format="DD.MM.YYYY"
                @input="input('sold_from', $event)"
            />
          </div>

          <div class="col-xs-4">
            <DateFormGroup
                label="Sold To"
                :value="filters.sold_to"
                format="DD.MM.YYYY"
                @input="input('sold_to', $event)"
            />
          </div>

          <div class="col-xs-2">
            <label>&nbsp;</label>

            <div class="btn-group tw-w-full">
              <button type="button" class="btn btn-white btn-block dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-clock"></i> <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li v-for="periodOption in periodOptions">
                  <a href="#" @click.prevent="setPeriod('sold', periodOption)">
                    {{ periodOption.label }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-xs-4">
            <DateFormGroup
                label="Published From"
                :value="filters.published_from"
                format="DD.MM.YYYY"
                @input="input('published_from', $event)"
            />
          </div>

          <div class="col-xs-4">
            <DateFormGroup
                label="Published To"
                :value="filters.published_to"
                format="DD.MM.YYYY"
                @input="input('published_to', $event)"
            />
          </div>

          <div class="col-xs-2">
            <label>&nbsp;</label>

            <div class="btn-group tw-w-full">
              <button type="button" class="btn btn-white btn-block dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-clock"></i> <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li v-for="periodOption in periodOptions">
                  <a href="#" @click.prevent="setPeriod('published', periodOption)">
                    {{ periodOption.label }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-xs-4">
            <div class="form-group">
              <label>&nbsp;</label>

              <button type="button" class="btn btn-link btn-block" @click="$emit('reset')">
                Reset...
              </button>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xs-5">
            <MultiSelectFormGroup
                label="User"
                :value="filters.user_id"
                :options="userOptions"
                @input="input('user_id', $event)"
            />
          </div>

          <div class="col-xs-5">
            <MultiSelectFormGroup
                label="Portal"
                :value="filters.portal_id"
                :options="portalOptions"
                @input="input('portal_id', $event)"
            />
          </div>

          <div class="col-xs-5">
            <MultiSelectFormGroup
                label="Channel"
                :value="filters.channel"
                :options="channelOptions"
                @input="input('channel', $event)"
            />
          </div>

          <div class="col-xs-5">
            <MultiSelectFormGroup
                label="Ad Format"
                :value="filters.ad_format"
                :options="adFormatOptions"
                @input="input('ad_format', $event)"
            />
          </div>

          <div class="col-xs-4">
            <div class="form-group">
              <label>&nbsp;</label>

              <button type="submit" class="btn btn-block btn-black">
                <i class="fa fa-filter"></i> Filter
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import store from '@/store'
import DateFormGroup from '@/vendor/forms/FormGroups/Block/Date'
import MultiSelectFormGroup from '@/components/FormGroups/Block/MultiSelect'
import SelectFormGroup from '@/vendor/forms/FormGroups/Block/Select'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Block/Typeahead'

export default {
  components: {DateFormGroup, MultiSelectFormGroup, SelectFormGroup, TypeaheadFormGroup},

  model: {
    prop: 'filters',
    event: 'filters-changed'
  },

  props: {
    title: {required: true},
    display: {default: true},
    filters: {required: true},
    showCompareButton: {default: false},
    users: {required: true},
    portals: {required: true},
    channels: {required: true},
    adFormats: {required: true}
  },

  data() {
    return {
      shared: store
    }
  },

  computed: {
    userOptions() {
      let users = this.users.data.concat([])

      users.sort(function (a, b) {
        return ('' + a.attributes.full_name).localeCompare(b.attributes.full_name);
      })

      return users.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    portalOptions() {
      let portals = this.portals.data.concat([])

      portals.sort(function (a, b) {
        return ('' + a.attributes.name).localeCompare(b.attributes.name);
      })

      return portals.map(portal => {
        return {
          value: portal.id,
          label: portal.attributes.name
        }
      })
    },

    channelOptions() {
      let channels = this.channels.data.concat([])

      channels.sort(function (a, b) {
        return ('' + a.name).localeCompare(b.name);
      })

      return channels.map(channel => {
        return {
          value: channel.type + '-' + channel.id,
          label: channel.name
        }
      })
    },

    adFormatOptions() {
      return this.adFormats.map(adFormat => {
        return {
          value: adFormat.attributes.category + '-' + adFormat.id,
          label: adFormat.attributes.name
        }
      })
    },

    periodOptions() {
      return [
        {
          value: 'this-week',
          label: 'This week',
          from: moment().startOf('isoWeek').format('YYYY-MM-DD'),
          to: moment().endOf('isoWeek').format('YYYY-MM-DD')
        },
        {
          value: 'year-to-date',
          label: 'Year-to-date',
          from: moment().startOf('year').format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD')
        },
        {
          value: 'this-year',
          label: 'This year',
          from: moment().startOf('year').format('YYYY-MM-DD'),
          to: moment().endOf('year').format('YYYY-MM-DD')
        },
        {
          value: 'last-year',
          label: 'Last year',
          from: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
          to: moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
        },
        {
          value: 'next-year',
          label: 'Next year',
          from: moment().add(1, 'year').startOf('year').format('YYYY-MM-DD'),
          to: moment().add(1, 'year').endOf('year').format('YYYY-MM-DD')
        },
        {
          value: 'this-month',
          label: 'This month',
          from: moment().startOf('month').format('YYYY-MM-DD'),
          to: moment().endOf('month').format('YYYY-MM-DD')
        },
        {
          value: 'last-month',
          label: 'Last month',
          from: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
        },
      ]
    }
  },

  methods: {
    input(key, value) {
      this.$emit('filters-changed', {...this.filters, [key]: value})
    },

    setPeriod(key, periodOption) {
      this.$emit('filters-changed', {
        ...this.filters,
        [key + '_from']: periodOption.from,
        [key + '_to']: periodOption.to
      })

      this.$emit('submit')
    },
  }
}
</script>
